import React, { useState, useEffect } from 'react';
import { supportTicketService } from '../../../services/supportTicketService';
import { MessageSquare, Clock, AlertCircle, CheckCircle, Loader, Send, ArrowLeft } from 'lucide-react';

const SupportTicketsTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSubscription, setMessageSubscription] = useState(null);

  useEffect(() => {
    loadTickets();
  }, []);

  // Real-time subscription for messages when viewing a ticket
  useEffect(() => {
    if (selectedTicket?.id) {
      // Subscribe to new messages
      const subscription = supportTicketService?.subscribeToTicketMessages(
        selectedTicket?.id,
        (newMessage) => {
          console.log('[SupportTicketsTab] Received new message:', newMessage);
          setMessages((prev) => [...prev, newMessage]);
          
          // Auto-scroll to bottom when new message arrives
          setTimeout(() => {
            const messagesContainer = document.getElementById('admin-messages-container');
            if (messagesContainer) {
              messagesContainer.scrollTop = messagesContainer?.scrollHeight;
            }
          }, 100);
        }
      );
      
      setMessageSubscription(subscription);
      
      // Cleanup subscription on unmount or ticket change
      return () => {
        if (subscription) {
          supportTicketService?.unsubscribeFromTicketMessages(subscription);
        }
      };
    }
  }, [selectedTicket?.id]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await supportTicketService?.getAllTickets();
      setTickets(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    setSelectedTicket(ticket);
    setNewMessage('');
    await loadMessages(ticket?.id);
  };

  const loadMessages = async (ticketId) => {
    try {
      setLoadingMessages(true);
      const data = await supportTicketService?.getTicketMessages(ticketId);
      setMessages(data);
    } catch (err) {
      setError(err?.message || 'Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage?.trim()) {
      return;
    }

    try {
      setSendingMessage(true);
      await supportTicketService?.createTicketMessage(selectedTicket?.id, newMessage);
      setNewMessage('');
      // Note: Real-time subscription will handle adding the new message to the list
    } catch (err) {
      setError(err?.message || 'Failed to send message');
      // If real-time fails, manually reload messages
      await loadMessages(selectedTicket?.id);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBackToList = () => {
    setSelectedTicket(null);
    setMessages([]);
    setNewMessage('');
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await supportTicketService?.updateTicketStatus(ticketId, newStatus);
      await loadTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (err) {
      setError(err?.message || 'Failed to update ticket status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'in_progress': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'closed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return null;
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTickets = statusFilter === 'all' 
    ? tickets 
    : tickets?.filter(t => t?.status === statusFilter);

  // Render message thread view
  if (selectedTicket) {
    return (
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={handleBackToList}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to All Tickets
        </button>

        {/* Ticket header */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                {getStatusIcon(selectedTicket?.status)}
                <h3 className="text-2xl font-bold text-gray-900">{selectedTicket?.subject}</h3>
              </div>
              <p className="text-gray-600 mb-4">{selectedTicket?.description}</p>
              <div className="flex items-center gap-4 text-sm">
                <span className={`px-3 py-1 rounded-full font-medium ${getUrgencyColor(selectedTicket?.urgency)}`}>
                  {selectedTicket?.urgency?.charAt(0)?.toUpperCase() + selectedTicket?.urgency?.slice(1)}
                </span>
                <span className="text-gray-500">
                  Client: {selectedTicket?.userName} ({selectedTicket?.userEmail})
                </span>
                <span className="text-gray-400">
                  Created: {new Date(selectedTicket?.createdAt)?.toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="ml-4">
              <select
                value={selectedTicket?.status}
                onChange={(e) => handleStatusChange(selectedTicket?.id, e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Messages Thread */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 500px)' }}>
          <div className="p-6 border-b border-gray-200">
            <h4 className="text-xl font-semibold text-gray-900">Conversation</h4>
          </div>

          {/* Messages list */}
          <div 
            id="admin-messages-container"
            className="flex-1 overflow-auto p-6 space-y-4"
          >
            {loadingMessages ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading messages...</p>
              </div>
            ) : messages?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages?.map((msg) => (
                <div
                  key={msg?.id}
                  className={`flex ${msg?.isAdmin ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-2xl rounded-lg p-4 ${
                    msg?.isAdmin 
                      ? 'bg-blue-50 border border-blue-200' :'bg-gray-100 border border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-sm text-gray-900">
                        {msg?.senderName}
                      </span>
                      {msg?.isAdmin && (
                        <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                          Admin
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {new Date(msg?.createdAt)?.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700">{msg?.message}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Message input */}
          <div className="p-4 border-t border-gray-200">
            <form onSubmit={handleSendMessage} className="flex gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                placeholder="Type your response..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={sendingMessage}
              />
              <button
                type="submit"
                disabled={sendingMessage || !newMessage?.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {sendingMessage ? 'Sending...' : 'Send'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Support Tickets</h2>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e?.target?.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Tickets</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      {/* Tickets list */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-gray-600">Loading tickets...</p>
          </div>
        ) : filteredTickets?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No tickets found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTickets?.map((ticket) => (
              <div 
                key={ticket?.id}
                onClick={() => handleTicketClick(ticket)}
                className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(ticket?.status)}
                      <h3 className="text-lg font-semibold text-gray-900">{ticket?.subject}</h3>
                      <MessageSquare className="w-4 h-4 text-blue-500" />
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{ticket?.description}</p>
                    <p className="text-sm text-gray-500">
                      Client: {ticket?.userName} ({ticket?.userEmail})
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  <span className={`px-3 py-1 rounded-full font-medium ${getUrgencyColor(ticket?.urgency)}`}>
                    {ticket?.urgency?.charAt(0)?.toUpperCase() + ticket?.urgency?.slice(1)}
                  </span>
                  <span className="text-gray-500">
                    {ticket?.status?.replace('_', ' ')?.charAt(0)?.toUpperCase() + ticket?.status?.replace('_', ' ')?.slice(1)}
                  </span>
                  <span className="text-gray-400">
                    {new Date(ticket?.createdAt)?.toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketsTab;